/**
 * JOY GSAP ANIMATIONS v3.0 — Site-wide (SAFE)
 *
 * CRITICAL SAFETY RULES:
 * 1. NEVER set opacity on elements via gsap.from/fromTo — this hides content
 *    if ScrollTrigger doesn't fire or fires late.
 * 2. Only use TRANSFORM-based animations (y, x, scale) for scroll reveals.
 * 3. Hover effects are CSS-class-driven or transform-only.
 * 4. Respects prefers-reduced-motion.
 */
(function () {
  "use strict";

  if (typeof gsap === "undefined") return;

  var prefersReduced =
    window.matchMedia &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  if (prefersReduced) return;

  var hasScrollTrigger = typeof ScrollTrigger !== "undefined";
  if (hasScrollTrigger) gsap.registerPlugin(ScrollTrigger);

  // ============================================
  // SCROLL REVEAL — transform-only, NO opacity
  // Elements slide up into place but are always visible.
  // ============================================

  function scrollSlideUp(selector, distance) {
    if (!hasScrollTrigger) return;
    var d = distance || 20;
    gsap.utils.toArray(selector).forEach(function (el) {
      gsap.set(el, { y: d });
      ScrollTrigger.create({
        trigger: el,
        start: "top 93%",
        once: true,
        onEnter: function () {
          gsap.to(el, { y: 0, duration: 0.6, ease: "power3.out" });
        },
      });
    });
  }

  function staggerSlideUp(parentSelector, childSelector, distance) {
    if (!hasScrollTrigger) return;
    var d = distance || 20;
    document.querySelectorAll(parentSelector).forEach(function (parent) {
      var children = gsap.utils.toArray(parent.querySelectorAll(childSelector));
      if (!children.length) return;
      gsap.set(children, { y: d });
      ScrollTrigger.create({
        trigger: parent,
        start: "top 93%",
        once: true,
        onEnter: function () {
          gsap.to(children, {
            y: 0,
            duration: 0.5,
            stagger: 0.06,
            ease: "power3.out",
          });
        },
      });
    });
  }

  // ============================================
  // HOVER HELPERS — transform only, no layout shift
  // ============================================

  function hoverScale(selector, scale) {
    var s = scale || 1.05;
    document.querySelectorAll(selector).forEach(function (el) {
      el.addEventListener("mouseenter", function () {
        gsap.to(this, { scale: s, duration: 0.25, ease: "power2.out" });
      });
      el.addEventListener("mouseleave", function () {
        gsap.to(this, { scale: 1, duration: 0.25, ease: "power2.out" });
      });
    });
  }

  function hoverLift(selector) {
    document.querySelectorAll(selector).forEach(function (el) {
      el.addEventListener("mouseenter", function () {
        gsap.to(this, { y: -4, duration: 0.3, ease: "power2.out" });
      });
      el.addEventListener("mouseleave", function () {
        gsap.to(this, { y: 0, duration: 0.3, ease: "power2.out" });
      });
    });
  }

  // ============================================
  // HEADER — gentle scale on logo, hover on icons
  // ============================================
  function animateHeader() {
    var logo = document.querySelector(
      ".header__heading-logo, .header__heading-link"
    );
    if (logo) {
      gsap.from(logo, {
        scale: 0.96,
        duration: 0.4,
        ease: "power2.out",
        delay: 0.1,
        clearProps: "scale",
      });
    }
    hoverScale(".header__icon", 1.12);
  }

  // ============================================
  // HERO / BANNERS — slide text up only, NO opacity
  // ============================================
  function animateHeroBanners() {
    var textEls = document.querySelectorAll(
      ".banner__heading, .banner__text, .banner__buttons, .slideshow__heading, .slideshow__text"
    );
    if (textEls.length) {
      gsap.from(textEls, {
        y: 15,
        duration: 0.5,
        stagger: 0.08,
        ease: "power2.out",
        delay: 0.15,
        clearProps: "y",
      });
    }
  }

  // ============================================
  // CTA PARALLAX — scrub-based, fully safe
  // ============================================
  function animateCtaParallax() {
    if (!hasScrollTrigger) return;
    document.querySelectorAll(".cta-parallax-section").forEach(function (section) {
      var bg = section.querySelector(".cta-parallax-bg, .cta-parallax-image");
      if (bg) {
        gsap.fromTo(bg, { yPercent: -6 }, {
          yPercent: 6,
          ease: "none",
          scrollTrigger: {
            trigger: section,
            start: "top bottom",
            end: "bottom top",
            scrub: true,
          },
        });
      }
    });
  }

  // ============================================
  // COLLECTION GRIDS — slide up cards
  // ============================================
  function animateCollections() {
    scrollSlideUp(".collection-hero__content, .collection-banner__content", 15);
    scrollSlideUp(".collection-grid-heading, .collection-list-title", 12);
    staggerSlideUp(".collection-grid, .collection-grid-main", ".collection-card", 25);
    staggerSlideUp(".collection-list, .collection-list-wrapper", ".collection-list__item, li.grid__item", 20);
    hoverLift(".collection-card");
  }

  // ============================================
  // PRODUCT CARDS & GRIDS
  // ============================================
  function animateProductCards() {
    staggerSlideUp(
      ".product-grid, .joy-related-grid, .product-grid-section .product-grid",
      ".joy-product-card, .product-card-grid, li.grid__item, .card-wrapper",
      20
    );

    staggerSlideUp(
      ".featured-collection .product-grid, .collection .product-grid",
      "li.grid__item",
      20
    );

    hoverLift(".joy-product-card, .product-card-grid");

    document.querySelectorAll(".product-card-grid").forEach(function (card) {
      var secondary = card.querySelector(".product-card-image-secondary");
      if (secondary) {
        card.addEventListener("mouseenter", function () {
          gsap.to(secondary, { opacity: 1, duration: 0.35, ease: "power2.out" });
        });
        card.addEventListener("mouseleave", function () {
          gsap.to(secondary, { opacity: 0, duration: 0.35, ease: "power2.out" });
        });
      }
    });

    hoverScale(
      ".product-card-icon, .joy-product-card__add-to-cart, .joy-product-card__wishlist",
      1.15
    );
  }

  // ============================================
  // FEATURED COLLECTION
  // ============================================
  function animateFeaturedCollection() {
    scrollSlideUp(
      ".collection__title, .featured-collection .title, .product-grid-heading",
      12
    );
    scrollSlideUp(".collection__description", 10);
  }

  // ============================================
  // IMAGE WITH TEXT / MULTIROW / MULTICOLUMN
  // ============================================
  function animateImageWithText() {
    if (!hasScrollTrigger) return;

    document.querySelectorAll(".image-with-text").forEach(function (section) {
      var media = section.querySelector(".image-with-text__media-item, .image-with-text__media");
      var content = section.querySelector(".image-with-text__content");

      if (media) {
        gsap.set(media, { x: -20 });
        ScrollTrigger.create({
          trigger: media,
          start: "top 90%",
          once: true,
          onEnter: function () {
            gsap.to(media, { x: 0, duration: 0.6, ease: "power3.out" });
          },
        });
      }
      if (content) {
        gsap.set(content, { x: 20 });
        ScrollTrigger.create({
          trigger: content,
          start: "top 90%",
          once: true,
          onEnter: function () {
            gsap.to(content, { x: 0, duration: 0.6, ease: "power3.out" });
          },
        });
      }
    });

    staggerSlideUp(
      ".multicolumn-list, .multicolumn",
      ".multicolumn-list__item, .multicolumn-card",
      20
    );

    scrollSlideUp(".multicolumn__title", 12);

    document.querySelectorAll(".multirow__inner").forEach(function (container) {
      var rows = gsap.utils.toArray(container.querySelectorAll(".image-with-text"));
      rows.forEach(function (row, i) {
        var dir = i % 2 === 0 ? -15 : 15;
        gsap.set(row, { x: dir });
        ScrollTrigger.create({
          trigger: row,
          start: "top 90%",
          once: true,
          onEnter: function () {
            gsap.to(row, { x: 0, duration: 0.6, ease: "power3.out" });
          },
        });
      });
    });
  }

  // ============================================
  // RICH TEXT
  // ============================================
  function animateRichText() {
    staggerSlideUp(
      ".rich-text__blocks, .rich-text__wrapper",
      ".rich-text__heading, .rich-text__caption, .rich-text__text, .rich-text__buttons .button",
      12
    );
  }

  // ============================================
  // COLLAGE
  // ============================================
  function animateCollage() {
    staggerSlideUp(".collage", ".collage__item", 20);
  }

  // ============================================
  // BLOG / ARTICLES
  // ============================================
  function animateBlog() {
    staggerSlideUp(
      ".blog__posts, .blog .slider",
      ".blog__post, .article-card-wrapper, li.grid__item",
      20
    );
    scrollSlideUp(".blog__title", 12);
    hoverLift(".article-card-wrapper, .article-card");
  }

  // ============================================
  // PRODUCT TABS (Joy)
  // ============================================
  function animateTabs() {
    scrollSlideUp(".joy-tabs-section", 15);

    document.querySelectorAll(".joy-tabs__btn").forEach(function (btn) {
      btn.addEventListener("click", function () {
        var section = this.closest(".joy-tabs-section");
        if (!section) return;
        setTimeout(function () {
          var activePanel = section.querySelector(".joy-tabs__panel--active");
          if (activePanel) {
            gsap.from(activePanel, {
              y: 8,
              duration: 0.3,
              ease: "power2.out",
              clearProps: "y",
            });
          }
        }, 10);
      });
    });
  }

  // ============================================
  // ACCORDION / FAQ
  // ============================================
  function animateAccordion() {
    scrollSlideUp(".collapsible-content", 15);

    document.querySelectorAll(".accordion details").forEach(function (detail) {
      detail.addEventListener("toggle", function () {
        if (this.open) {
          var content = this.querySelector(".accordion__content");
          if (content) {
            gsap.from(content, {
              y: -5,
              duration: 0.25,
              ease: "power2.out",
              clearProps: "y",
            });
          }
        }
      });
    });
  }

  // ============================================
  // NEWSLETTER
  // ============================================
  function animateNewsletter() {
    scrollSlideUp(".newsletter, .joy-newsletter-section", 20);
  }

  // ============================================
  // CONTACT FORMS
  // ============================================
  function animateContactForms() {
    scrollSlideUp(
      ".contact, .contact-form-section, .contact-hero, .custom-orders-section",
      20
    );
    staggerSlideUp(
      "#ContactForm, .contact-form__wrapper, .custom-orders__wrapper",
      ".field, .form-group, .form-row",
      10
    );
  }

  // ============================================
  // BRAND STORY / ABOUT
  // ============================================
  function animateBrandStory() {
    if (!hasScrollTrigger) return;

    document.querySelectorAll(".brand-story-section").forEach(function (section) {
      var img = section.querySelector(".brand-story-image-wrapper");
      var content = section.querySelector(".brand-story-content");
      if (img) {
        gsap.set(img, { x: -20 });
        ScrollTrigger.create({
          trigger: img,
          start: "top 90%",
          once: true,
          onEnter: function () {
            gsap.to(img, { x: 0, duration: 0.6, ease: "power3.out" });
          },
        });
      }
      if (content) {
        gsap.set(content, { x: 20 });
        ScrollTrigger.create({
          trigger: content,
          start: "top 90%",
          once: true,
          onEnter: function () {
            gsap.to(content, { x: 0, duration: 0.6, ease: "power3.out" });
          },
        });
      }
    });

    scrollSlideUp(".about-story-section", 20);
    staggerSlideUp(
      ".about-story-section",
      ".about-image, .about-content, [class*='values-parent']",
      15
    );
  }

  // ============================================
  // SOCIAL MEDIA
  // ============================================
  function animateSocialMedia() {
    staggerSlideUp(
      ".social-media-section, .social-icons",
      ".social-icon, a.social-icon",
      10
    );
    hoverScale(".social-icon, a.social-icon", 1.15);
  }

  // ============================================
  // FOOTER
  // ============================================
  function animateFooter() {
    staggerSlideUp(".footer-grid, .footer-main", ".footer-column", 12);
  }

  // ============================================
  // VIDEO
  // ============================================
  function animateVideo() {
    scrollSlideUp(".video-section", 15);
    hoverScale(".deferred-media__poster-button", 1.08);
  }

  // ============================================
  // COLLECTION BANNER — gentle parallax only
  // ============================================
  function animateCollectionBanner() {
    if (!hasScrollTrigger) return;
    var heroImg = document.querySelector(".collection-hero__image");
    if (heroImg) {
      gsap.fromTo(heroImg, { scale: 1.04 }, {
        scale: 1,
        ease: "none",
        scrollTrigger: {
          trigger: heroImg.closest(".collection-hero, .collection-banner-hero") || heroImg,
          start: "top top",
          end: "bottom top",
          scrub: true,
        },
      });
    }
  }

  // ============================================
  // MYSTERY KEYCHAIN
  // ============================================
  function animateMysteryKeychain() {
    scrollSlideUp(".mystery-keychain-section", 20);
    staggerSlideUp(".mystery-keychain-thumbnails", ".mystery-thumbnail", 8);
  }

  // ============================================
  // RELATED PRODUCTS
  // ============================================
  function animateRelatedProducts() {
    scrollSlideUp(".related-products__heading, .joy-related__heading", 12);
    staggerSlideUp(
      "product-recommendations .product-grid, .joy-related-grid",
      "li.grid__item, .joy-product-card",
      20
    );
  }

  // ============================================
  // CART DRAWER
  // ============================================
  function animateCartDrawer() {
    var drawer = document.querySelector("cart-drawer .drawer");
    if (!drawer) return;
    var observer = new MutationObserver(function (mutations) {
      mutations.forEach(function (m) {
        if (m.target.classList && m.target.classList.contains("active")) {
          var items = drawer.querySelectorAll(".cart-item");
          if (items.length) {
            gsap.from(items, {
              x: 20,
              duration: 0.3,
              stagger: 0.04,
              ease: "power2.out",
              clearProps: "x",
            });
          }
        }
      });
    });
    observer.observe(drawer, { attributes: true, attributeFilter: ["class"] });
  }

  // ============================================
  // INIT
  // ============================================
  function init() {
    animateHeader();
    animateHeroBanners();
    animateCtaParallax();
    animateCollections();
    animateProductCards();
    animateFeaturedCollection();
    animateImageWithText();
    animateRichText();
    animateCollage();
    animateBlog();
    animateTabs();
    animateAccordion();
    animateNewsletter();
    animateContactForms();
    animateBrandStory();
    animateSocialMedia();
    animateFooter();
    animateVideo();
    animateCollectionBanner();
    animateMysteryKeychain();
    animateRelatedProducts();
    animateCartDrawer();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }

  document.addEventListener("shopify:section:load", function () {
    init();
    if (hasScrollTrigger) ScrollTrigger.refresh();
  });
})();
